import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, Play, ShieldCheck, FileText, ClipboardList, Glasses, School } from "lucide-react";
import { IMAGES } from "@/config/content";
import { KineticHeading } from "@/components/site/ui";
import { scrollToId } from "@/lib/scroll";

const Badge = ({ icon: Icon, label, className, delay }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.85, y: 12 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ delay, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    className={`absolute glass soft-shadow rounded-2xl px-3.5 py-2.5 flex items-center gap-2 ${className}`}
    data-cursor="hover"
  >
    <span className="grid place-items-center w-8 h-8 rounded-xl bg-brand-teal/12 text-brand-teal">
      <Icon className="w-4 h-4" />
    </span>
    <span className="text-[13px] font-semibold text-brand-navy whitespace-nowrap">{label}</span>
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBig = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
  const ySmall = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 140]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden pt-28 pb-20 lg:pt-36 lg:pb-28">
      {/* organic background shapes */}
      <motion.div style={{ y: yBlob }} className="pointer-events-none absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-brand-sky/20 blur-3xl animate-blob" />
      <div className="pointer-events-none absolute -bottom-40 -left-32 w-[480px] h-[480px] rounded-full bg-brand-teal/12 blur-3xl animate-blob" />
      {/* focus-line eye motif */}
      <svg className="pointer-events-none absolute top-24 left-1/2 -translate-x-1/2 opacity-[0.06] w-[900px] max-w-none" viewBox="0 0 900 400" fill="none" aria-hidden="true">
        <motion.ellipse cx="450" cy="200" rx="360" ry="150" stroke="#0A192F" strokeWidth="2"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.2, ease: "easeInOut" }} />
        <motion.circle cx="450" cy="200" r="70" fill="none" stroke="#0D9488" strokeWidth="2"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.6 }} />
      </svg>

      <div className="max-site container-px relative grid lg:grid-cols-12 gap-12 items-center">
        {/* copy */}
        <div className="lg:col-span-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-brand-teal/10 text-brand-teal px-5 py-2 text-sm font-bold tracking-wide uppercase"
          >
            <ShieldCheck className="w-4 h-4" /> Free, bulk-billed school &amp; childcare vision testing · NSW
          </motion.span>

          <KineticHeading
            className="mt-6 text-4xl sm:text-5xl lg:text-[4.2rem] font-extrabold leading-[0.98] tracking-tight text-brand-navy"
            lines={["Clearer Vision.", "Brighter Learning."]}
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-6 text-base sm:text-lg leading-relaxed text-brand-ink/85 max-w-xl"
          >
            Comprehensive vision assessments delivered directly to schools and childcare centres, helping identify vision concerns that may affect a child's learning, confidence and development.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.62, duration: 0.7 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <button onClick={() => scrollToId("contact")} data-testid="hero-book-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-brand-teal hover:bg-brand-tealdark text-white font-semibold px-6 py-3.5 shadow-xl shadow-brand-teal/25 transition-colors focus-ring">
              Book a School Vision Program
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => scrollToId("how-it-works")} data-testid="hero-how-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white text-brand-navy font-semibold px-6 py-3.5 border border-brand-navy/10 soft-shadow hover:border-brand-teal/40 transition-colors focus-ring">
              <Play className="w-4 h-4 text-brand-teal" /> See How It Works
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-6 text-sm text-brand-ink/70 flex items-center gap-2"
          >
            <span className="inline-block w-8 h-px bg-brand-teal" />
            Professional eye care delivered by experienced Orthoptists and Optometrists.
          </motion.p>
        </div>

        {/* layered image collage */}
        <div className="lg:col-span-6 relative">
          <div className="relative mx-auto max-w-[520px]">
            <motion.div style={{ y: yBig }} className="relative rounded-[2rem] overflow-hidden soft-shadow ring-1 ring-black/5">
              <img src={IMAGES.heroExam} alt="A Straight Sight orthoptist fitting a primary-school student with their new glasses at school" className="w-full h-[460px] object-cover" loading="eager" />
            </motion.div>

            <motion.div style={{ y: ySmall }} className="absolute -bottom-10 -left-6 w-44 sm:w-52 rounded-2xl overflow-hidden soft-shadow ring-1 ring-black/5 animate-float">
              <img src={IMAGES.teensGroup} alt="Smiling students proud of their new glasses" className="w-full h-40 object-cover" loading="lazy" />
            </motion.div>

            <Badge icon={School} label="School-Based Assessments" className="-top-4 -left-2 sm:-left-8" delay={0.7} />
            <Badge icon={FileText} label="Parent Reports" className="top-24 -right-4 sm:-right-10" delay={0.85} />
            <Badge icon={ClipboardList} label="Teacher Summaries" className="bottom-24 -right-4 sm:-right-10" delay={1.0} />
            <Badge icon={Glasses} label="Glasses Support" className="-bottom-6 right-10" delay={1.15} />
          </div>
        </div>
      </div>
    </section>
  );
};
